import { Badge } from '@/trailmix/Badge';
import { ProseDisplay } from '@/components/Tiptap';
import { getImageFromCDN } from '@/image-cdn';
import { Place, VisitPhoto } from '@/pages/types';
import { cn } from '@/trailmix/lib/cn';
import { PhotoGrid } from '@/trailmix/PhotoGrid';
import { Rating } from '@/trailmix/Rating';
import { Link } from '@inertiajs/react';
import { IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuItemProps, MenuList } from '@chakra-ui/react';
import Icon from '@mdi/react';
import { mdiDotsVertical } from '@mdi/js';

function formatDistance(meters: number | null | undefined): string | null {
    if (!meters) return null;
    const miles = meters / 1609.34;
    if (miles < 0.1) return `${Math.round(meters * 3.28084)} ft`;
    return `${miles.toFixed(1)} mi`;
}

export function PlaceDetail({
    place,
    rating,
    notes,
    photos,
    distanceAwayMeters,
    actions,
    menu,
    className,
}: {
    place: Place;
    rating?: number | null;
    notes?: string | null;
    photos?: VisitPhoto[];
    distanceAwayMeters?: number | null;
    actions?: React.ReactNode;
    menu?: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={cn(className)}>
            <div className='flex items-start justify-between gap-2'>
                <Link href={route('places.show', [place])} className='flex flex-1 flex-col items-start gap-0.5'>
                    <p className='text-lg font-semibold'>{place.name}</p>
                    <div className='flex items-center gap-2'>
                        <p className='line-clamp-1 text-sm text-gray-600'>{place.address}</p>
                        {distanceAwayMeters && <span className='text-gray-600'>•</span>}
                        {distanceAwayMeters && (
                            <p className='whitespace-nowrap text-sm font-medium text-blue-600'>
                                {formatDistance(distanceAwayMeters)} away
                            </p>
                        )}
                    </div>
                </Link>

                {menu}
            </div>

            {place.place_lists !== undefined && place.place_lists.length > 0 && (
                <div className='mt-4 flex flex-wrap items-center gap-2'>
                    {place.place_lists !== undefined &&
                        place.place_lists.map(placeList => (
                            <Badge
                                key={placeList.id}
                                variant='list'
                                href={route('lists.show', { user: placeList.user, placeList })}
                            >
                                {placeList.name}
                            </Badge>
                        ))}
                </div>
            )}

            {rating && (
                <div className='mt-4'>
                    <Rating value={rating} />
                </div>
            )}

            {notes && (
                <div className='mt-4'>
                    <ProseDisplay content={notes} />
                </div>
            )}

            {photos && photos.length > 0 && (
                <PhotoGrid
                    photos={photos.map(photo => ({
                        id: photo.id,
                        originalURL: getImageFromCDN({ path: photo.path, size: 'original' }).toURL(),
                        thumbnailURL: getImageFromCDN({ path: photo.path, size: 'medium' }).toURL(),
                    }))}
                    className='mt-6'
                />
            )}

            {actions && <div className='mt-6 flex flex-wrap gap-2'>{actions}</div>}
        </div>
    );
}

PlaceDetail.Menu = function PlaceDetailMenu({ children }: { children: React.ReactNode }) {
    return (
        <Menu>
            <MenuButton
                as={IconButton}
                aria-label='Options'
                icon={<Icon path={mdiDotsVertical} className='size-5' />}
                variant='ghost'
                size='sm'
            />
            <MenuList>{children}</MenuList>
        </Menu>
    );
};

PlaceDetail.MenuItem = function PlaceDetailMenuItem({
    children,
    iconName,
    href,
    onClick,
    variant = 'default',
}: MenuItemProps & {
    iconName?: string;
    href?: string;
    variant?: 'default' | 'danger';
}) {
    return (
        <MenuItem
            as={href ? Link : undefined}
            href={href}
            onClick={onClick}
            icon={iconName ? <Icon path={iconName} className='size-5' /> : undefined}
            color={variant === 'danger' ? 'red.600' : undefined}
            _hover={{
                bg: variant === 'danger' ? 'red.50' : 'gray.100',
            }}
        >
            {children}
        </MenuItem>
    );
};

PlaceDetail.MenuDivider = function PlaceDetailMenuDivider() {
    return <MenuDivider />;
};
